import React, { useState, useEffect, useCallback } from "react";
import "@mantine/core/styles.css";
import "./App.css";
import {
  AppShell,
  Burger,
  Group,
  NavLink,
  Text,
  Stack,
  Modal,
  Button,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useWindowScroll, useMediaQuery } from "@mantine/hooks";
import { CreateDocumentView } from "./components/CreateDocumentView";
import { SignDocumentView } from "./components/SignDocumentView";
import { ValidateDocumentView } from "./components/ValidateDocumentView";
import { KeyManagementView } from "./components/KeyManagementView";
import { MetaPageDebugger } from "./components/MetaPageDebugger";
import { LandingPage } from "./components/LandingPage";
import { VaultUnlockModal } from "./components/VaultUnlockModal";
import {
  getVaultStatus,
  lockVault,
  onVaultStatusChange,
  type VaultStatus,
} from "./lib/key-vault";
import type { SignatureAnnotation } from "./types";

// Tabs that require the vault to be unlocked
const VAULT_REQUIRED_TABS = new Set(["create", "sign", "keys"]);

function App() {
  const [opened, { toggle }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState("landing");
  const [annotations, setAnnotations] = useState<SignatureAnnotation[]>([]);
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);
  const [scroll] = useWindowScroll();
  const [scrolled, setScrolled] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Vault state
  const [vaultStatus, setVaultStatus] = useState<VaultStatus>("uninitialized");
  const [vaultModalOpen, setVaultModalOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [vaultIntent, setVaultIntent] = useState<"create" | "sign" | "keys">(
    "keys",
  );

  // Check vault status on mount and listen for changes
  useEffect(() => {
    getVaultStatus().then(setVaultStatus);
    const unsub = onVaultStatusChange(setVaultStatus);
    return unsub;
  }, []);

  useEffect(() => {
    setScrolled(scroll.y > 10);
  }, [scroll.y]);

  // Navigate to a tab, checking vault status if needed
  const navigateToTab = useCallback(
    (tab: string, file?: File | null) => {
      if (file) setPendingPdfFile(file);

      if (VAULT_REQUIRED_TABS.has(tab) && vaultStatus !== "unlocked") {
        // Vault needed — show unlock modal, remember where they wanted to go
        setPendingTab(tab);
        setVaultIntent(tab as "create" | "sign" | "keys");
        setVaultModalOpen(true);
      } else {
        setActiveTab(tab);
      }
    },
    [vaultStatus],
  );

  // When vault successfully unlocks, navigate to the pending tab
  const handleVaultUnlocked = useCallback(() => {
    setVaultModalOpen(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
  }, [pendingTab]);

  const handleVaultCancel = useCallback(() => {
    setVaultModalOpen(false);
    setPendingTab(null);
  }, []);

  const isVaultUnlocked = vaultStatus === "unlocked";

  return (
    <React.Fragment>

      {/* Vault unlock modal — non-blocking, shown only when needed */}
      <VaultUnlockModal
        opened={vaultModalOpen}
        onUnlocked={handleVaultUnlocked}
        onCancel={handleVaultCancel}
        intent={vaultIntent}
      />

      {/* Desktop Header - Plain CSS, no Mantine */}
      {isDesktop && (
        <header
          className={`custom-header ${scrolled ? "header-scrolled" : "header-top"}`}
        >
          <div className="header-content">
            <nav className="desktop-nav">
              <a
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("landing");
                }}
              >
                <span className="logo">Signed+</span>
              </a>
              <a
                className={activeTab === "create" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToTab("create");
                }}
              >
                Create
              </a>
              <a
                className={activeTab === "sign" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToTab("sign");
                }}
              >
                Sign
              </a>
              <a
                className={activeTab === "validate" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("validate");
                }}
              >
                Validate
              </a>
              <a
                className={activeTab === "keys" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToTab("keys");
                }}
              >
                Keys
              </a>
            </nav>
            <span className="version-text">v0.1.1</span>
            {isVaultUnlocked && (
              <Tooltip label="Lock Key Vault" position="bottom">
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    lockVault();
                    setActiveTab("landing");
                  }}
                  style={{ cursor: "pointer", fontSize: "14px", opacity: 0.7 }}
                >
                  🔒
                </a>
              </Tooltip>
            )}
          </div>
        </header>
      )}

      <AppShell
        header={!isDesktop ? { height: 60, offset: false } : { height: 0 }}
        navbar={{
          width: 240,
          breakpoint: "sm",
          collapsed: { desktop: true, mobile: !opened },
        }}
        padding={0}
      >
        {!isDesktop && (
          <AppShell.Header
            className={scrolled ? "header-scrolled" : "header-top"}
            style={{ position: "sticky", top: 0, zIndex: 100 }}
          >
            <div className="header-content">
              <Burger opened={opened} onClick={toggle} size="sm" />
              <Text fw={700} size="xl" style={{ letterSpacing: "-0.3px" }}>
                Signed+
              </Text>
              {isVaultUnlocked && (
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    lockVault();
                    setActiveTab("landing");
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "14px",
                    opacity: 0.7,
                    marginLeft: "auto",
                  }}
                >
                  🔒
                </a>
              )}
            </div>
          </AppShell.Header>
        )}

        <AppShell.Navbar
          p="xs"
          hiddenFrom="sm"
          style={{ top: "60px" } as React.CSSProperties}
        >
          <Stack gap="xs">
            <NavLink
              label="Home"
              active={activeTab === "landing"}
              onClick={() => {
                setActiveTab("landing");
                toggle();
              }}
              variant="filled"
              color="blue"
            />
            <NavLink
              label="Create"
              active={activeTab === "create"}
              onClick={() => {
                navigateToTab("create");
                toggle();
              }}
              variant="filled"
              color="blue"
            />
            <NavLink
              label="Sign"
              active={activeTab === "sign"}
              onClick={() => {
                navigateToTab("sign");
                toggle();
              }}
              variant="filled"
              color="blue"
            />
            <NavLink
              label="Validate"
              active={activeTab === "validate"}
              onClick={() => {
                setActiveTab("validate");
                toggle();
              }}
              variant="filled"
              color="blue"
            />
            <NavLink
              label="Keys"
              active={activeTab === "keys"}
              onClick={() => {
                navigateToTab("keys");
                toggle();
              }}
              variant="filled"
              color="blue"
            />
            <NavLink
              label="🔧 Meta Page Debug"
              active={activeTab === "debug"}
              onClick={() => {
                setActiveTab("debug");
                toggle();
              }}
              variant="filled"
              color="grape"
            />
          </Stack>
        </AppShell.Navbar>

        <AppShell.Main
          pt={isDesktop ? 60 : 0}
          style={{ background: "#f0f0f0", minHeight: "100vh" }}
        >
          {activeTab === "landing" && (
            <LandingPage
              onGetStarted={() => {
                navigateToTab("create");
              }}
              onPdfDetected={(file, hasMetadata, isComplete) => {
                if (hasMetadata) {
                  if (isComplete) {
                    setActiveTab("validate");
                    setPendingPdfFile(file);
                  } else {
                    navigateToTab("sign", file);
                  }
                } else {
                  navigateToTab("create", file);
                }
              }}
            />
          )}
          {activeTab === "create" && (
            <CreateDocumentView
              annotations={annotations}
              setAnnotations={setAnnotations}
              initialFile={pendingPdfFile}
              onFileLoaded={() => setPendingPdfFile(null)}
              onNavigate={(tab, file) => {
                navigateToTab(tab, file);
              }}
            />
          )}
          {activeTab === "sign" && (
            <SignDocumentView
              initialFile={pendingPdfFile}
              onFileLoaded={() => setPendingPdfFile(null)}
              onNavigate={(tab, file) => {
                navigateToTab(tab, file);
              }}
            />
          )}
          {activeTab === "validate" && (
            <ValidateDocumentView
              initialFile={pendingPdfFile}
              onFileLoaded={() => setPendingPdfFile(null)}
            />
          )}
          {activeTab === "keys" && <KeyManagementView />}
          {activeTab === "debug" && <MetaPageDebugger />}
        </AppShell.Main>
      </AppShell>
    </React.Fragment>
  );
}

export default App;
